import { useCallback, useEffect, useMemo, useState } from 'react';
import { uniqBy } from 'lodash-es';

import type { ContactFormItem } from '@/features/contacts/contacts';
import { sortContactsByPrimaryAndFirstName } from '@/features/contacts/helpers/sorter';

import {
  companyContactDeletedIdState,
  companyContactsAtomState,
  companyContactsEditModeState,
  companyContactsPageState,
  companyIdState,
  isMoreContactsThanMaxVisibleAtom,
} from '../company.state';
import { useDeleteCompanyContact } from './use-delete-company-contact.mutation';
import { useResetAtom } from 'jotai/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCompanyContactsQuery } from './use-company-contacts.query';

export const MAX_VISIBLE_CONTACTS_NUMBER = 6;

export const useCompanyContacts = () => {
  const { data, refetch } = useCompanyContactsQuery();
  const [showAllContacts, setShowAllContacts] = useState(false);
  const editMode = useAtomValue(companyContactsEditModeState);

  const { deleteContact } = useDeleteCompanyContact();

  const companyId = useAtomValue(companyIdState);
  const isMoreContactsThanMaxVisible = useAtomValue(
    isMoreContactsThanMaxVisibleAtom,
  );
  const [contacts, setContacts] = useAtom(companyContactsAtomState);

  const { pageSize } = useAtomValue(companyContactsPageState);
  const setDeletedContact = useSetAtom(companyContactDeletedIdState);
  const resetPagination = useResetAtom(companyContactsPageState);
  const resetEditContactState = useResetAtom(companyContactsEditModeState);
  const [resetContacts, setResetContacts] = useState(false);

  useEffect(() => {
    resetPagination();
    resetEditContactState();
    setContacts([]);
    setShowAllContacts(false);
    setResetContacts(false);
  }, [companyId, resetPagination, setContacts, resetEditContactState]);

  useEffect(() => {
    if (resetContacts) return;
    if (!data) return;

    if (data.contacts.length === 0) {
      setContacts([]);
      return;
    }

    setContacts(old =>
      sortContactsByPrimaryAndFirstName(
        uniqBy([...old, ...data.contacts], contact => contact.id),
      ),
    );
  }, [data, setContacts, resetContacts]);

  useEffect(() => {
    if (!resetContacts) return;
    if (!data) return;

    setContacts(
      sortContactsByPrimaryAndFirstName(
        uniqBy(data.contacts || [], contact => contact.id),
      ),
    );
    resetPagination();
    setResetContacts(false);
  }, [data, resetContacts, setContacts, resetPagination]);

  const visibleContacts = useMemo(() => {
    if (editMode || showAllContacts) {
      return contacts;
    }
    return contacts.slice(0, MAX_VISIBLE_CONTACTS_NUMBER);
  }, [contacts, editMode, showAllContacts]);

  const reset = useCallback(() => {
    resetEditContactState();
    void refetch();
    setResetContacts(true);
  }, [refetch, resetEditContactState]);

  const addContactList = useCallback(
    (contacts: ContactFormItem[]) => {
      setContacts(currentContacts =>
        sortContactsByPrimaryAndFirstName([...currentContacts, ...contacts]),
      );
    },
    [setContacts],
  );

  const editContactList = useCallback(
    (editedContacts: ContactFormItem[]) => {
      setContacts(contacts => {
        const updatedContacts = contacts.map(oldContact => {
          const editedContact = editedContacts.find(
            editedContact => editedContact.id === oldContact.id,
          );

          if (editedContact) return editedContact;

          return oldContact;
        });

        return sortContactsByPrimaryAndFirstName(updatedContacts);
      });
    },
    [setContacts],
  );

  const handleDeleteContact = useCallback(
    async (contactId: string, synced: boolean) => {
      if (synced && companyId) {
        await deleteContact({
          companyId,
          companyContactId: contactId,
        });
      }

      setDeletedContact(contactId);
      setContacts(contacts =>
        contacts.filter(oldContact => {
          return oldContact.id !== contactId;
        }),
      );
    },
    [companyId, setContacts, setDeletedContact, deleteContact],
  );

  const hasNextPage = data?.contacts && data?.contacts.length >= pageSize;
  const poweredByCrunchbase = data?.poweredBy === 'crunchbase';

  const toggleAllContacts = useCallback(() => {
    setShowAllContacts(val => !val);
  }, []);

  return {
    allContacts: contacts,
    visibleContacts,
    isLoading: !data,
    hasNextPage,
    addContactList,
    editContactList,
    deleteContact: handleDeleteContact,
    reset,
    isMoreContactsThanMaxVisible,
    toggleAllContacts,
    allContactsVisible: showAllContacts,
    poweredByCrunchbase,
    lastEditedAt: data?.lastEditedAt ?? undefined,
  };
};
